import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../api/apiRoutes"; // Asegúrate que la ruta sea correcta
import { apiFetchJson } from "../../utils/http";

const PAGE_SIZE = 50; // Un poco menos que productos para no saturar si trae mucha data
const DEBOUNCE_MS = 300;

export default function CertificadoAutocomplete({
  value,
  onChange,
  disabled = false,
  placeholder = "Buscar certificado...",
}) {
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  // ============================
  // ESTADOS
  // ============================
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============================
  // SINCRONIZAR VALUE → INPUT
  // ============================
  useEffect(() => {
    // Si recibimos un objeto certificado, mostramos su numero
    if (value?.numero) {
      setInputText(value.numero);
    } else if (!value) {
      setInputText("");
    }
  }, [value]);

  // ============================
  // CLICK AFUERA → CERRAR
  // ============================
  useEffect(() => {
    const handleOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  // ============================
  // FETCH
  // ============================
  const fetchCertificados = async ({ pageIndex = 0, append = false, query }) => {
    if (loading) return;
    setLoading(true);

    try {
      // Usamos el listUrl que ya configuramos en apiRoutes
      // Mapeamos el texto del input al filtro "numero"
      const url = apiRoutes.certificados.listUrl({
        page: pageIndex,
        size: PAGE_SIZE,
        numero: query // <--- Filtramos por número
      });

      const response = await apiFetchJson(url); // apiFetchJson o appFetchJson según tu proyecto
      const responseItems = response?.items ?? [];

      setItems((prev) =>
        append ? [...prev, ...responseItems] : responseItems
      );
      
      // Calculamos si hay más páginas
      const totalElements = response?.totalElements || response?.total || 0;
      const currentLoaded = (pageIndex * PAGE_SIZE) + responseItems.length;
      setHasMore(currentLoaded < totalElements);
      
      setPage(pageIndex);
    } catch (e) {
      console.error(e);
      if (!append) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================
  // INPUT (debounce)
  // ============================
  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    setOpen(true);

    // Si borra todo, limpiamos la selección
    if (value && text.trim() === "") {
      onChange(null);
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchCertificados({
        pageIndex: 0,
        append: false,
        query: text.trim(),
      });
    }, DEBOUNCE_MS);
  };

  const handleFocus = () => {
    setOpen(true);
    // Carga inicial al enfocar, usando lo que haya escrito
    fetchCertificados({
      pageIndex: 0,
      append: false,
      query: inputText.trim(),
    });
  };

  // ============================
  // SCROLL INFINITO
  // ============================
  const handleScroll = (e) => {
    const el = e.currentTarget;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && hasMore && !loading) {
      fetchCertificados({
        pageIndex: page + 1,
        append: true,
        query: inputText.trim(),
      });
    }
  };

  // ============================
  // SELECCIONAR
  // ============================
  const handleSelect = (cert) => {
    onChange(cert); // Devolvemos el objeto completo al padre
    setInputText(cert.numero);
    setOpen(false);
  };

  // ============================
  // LIMPIAR
  // ============================
  const clearSelection = () => {
    onChange(null);
    setInputText("");
    setItems([]);
    setOpen(false);
  };

  // ============================
  // RENDER
  // ============================
  return (
    <div ref={boxRef} className="relative w-full">
      <input
        type="text"
        className="input w-full" // Asegúrate de tener la clase 'input' o usa estilos tailwind
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={handleInputChange}
        onFocus={handleFocus}
      />

      {value && !disabled && (
        <button
          type="button"
          onClick={clearSelection}
          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
          title="Limpiar"
        >
          ×
        </button>
      )}

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
          onScroll={handleScroll}
        >
          {loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">Cargando...</div>
          )}

          {!loading && items.length === 0 && (
            <div className="px-3 py-2 text-gray-500">Sin resultados.</div>
          )}

          {items.map((c) => (
            <button
              key={c.id}
              type="button"
              className="block w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-50 last:border-0"
              onClick={() => handleSelect(c)}
            >
              <div className="font-medium text-gray-900">
                {c.numero}
              </div>
              <div className="text-xs text-gray-500">
                Fecha: {c.data ? c.data.substring(0, 10) : "Sin fecha"}
              </div>
            </button>
          ))}

          {loading && items.length > 0 && (
            <div className="px-3 py-2 text-gray-400 text-xs text-center">
              Cargando más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}